import { BATTLESHIP } from 'HubStyleTokens/colors';
import FormattedMessage from 'I18n/components/FormattedMessage';
import HR from 'UIComponents/elements/HR';
import SuiteProductLevelIdentifiersMap from 'pricing-pages-ui/constants/merchCards/SuiteProductLevelIdentifiersMap';
import { hasPricingAndPurchasingSucceededSelector } from 'pricing-pages-ui/selectors/LoadingStateSelectors';
import { ENTERPRISE, FREE, PROFESSIONAL, STARTER } from 'self-service-api/constants/ProductLevels';
import styled from 'styled-components';
import { SUITE_CARD } from '../../../constants/tracking/Locations';
import { useAppSelector } from '../../../reducers';
import { ownsCrmSuiteProductLevel } from '../../../selectors/ProductInventorySelectors';
import { currentSuiteSkuIdsForProductLevelSelector } from '../../../selectors/ProductsSelectors';
import { useQueryParamTerm } from '../../../utils/urls/appQueryParamUtils';
import CurrentPlanTag from '../../common/CurrentPlanTag';
import { SectionTitle } from '../../common/SharedComponents';
import SkeletonLoadWrapper from '../../common/loading/SkeletonLoadWrapper';
import IncludedSeatsDisplay from '../../merch-card/IncludedSeatsDisplay';
import { Section, borderStyle } from '../../merch-card/MerchCardSharedComponents';
import SuiteTermSelectorContainer from '../../merch-card/SuiteTermSelectorContainer';
import SuiteTileCTAsContainer from '../../merch-card/SuiteTileCTAsContainer';
import { useShowPricePerMonthPerSeat } from '../../merch-card/merchCardUtils';
import { CrmSuitePrice } from '../../merch-card/price-display/CrmSuitePrice';
import MobileSuiteBusinessObjectives from '../MobileSuiteBusinessObjectives';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
export const StyledHR = styled(HR).withConfig({
  displayName: "MobileSuiteTile__StyledHR",
  componentId: "isppq8-0"
})(["background-color:", ";"], BATTLESHIP);
const getStringKeysForProductLevel = productLevel => {
  switch (productLevel) {
    case FREE:
      return {
        titleKey: 'merchCards.suiteTile.freeCard.title',
        subtitleKey: 'merchCards.suiteTile.freeCard.description'
      };
    case STARTER:
      return {
        titleKey: 'merchCards.suiteTile.starterCard.title',
        subtitleKey: 'merchCards.suiteTile.starterCard.description'
      };
    case PROFESSIONAL:
      return {
        titleKey: 'merchCards.suiteTile.professionalCard.title',
        subtitleKey: 'merchCards.suiteTile.professionalCard.description'
      };
    case ENTERPRISE:
      return {
        titleKey: 'merchCards.suiteTile.enterpriseCard.title',
        subtitleKey: 'merchCards.suiteTile.enterpriseCard.description'
      };
    default:
      return {
        titleKey: '',
        subtitleKey: ''
      };
  }
};
export const MobileSuiteTile = ({
  productLevel,
  commonStyles
}) => {
  const hasPricingAndPurchasingSucceeded = useAppSelector(hasPricingAndPurchasingSucceededSelector);
  const isCurrentPlanLevel = useAppSelector(state => ownsCrmSuiteProductLevel(state, {
    productLevel
  }));
  const {
    setQueryParamTerm,
    queryParamTerm
  } = useQueryParamTerm();
  const {
    titleKey,
    subtitleKey
  } = getStringKeysForProductLevel(productLevel);
  const skuIds = useAppSelector(state => {
    if (!hasPricingAndPurchasingSucceeded) return [];
    return currentSuiteSkuIdsForProductLevelSelector(state, {
      productLevel
    });
  });

  // For showing price/mo/seat and included seats, only show for a bundled suite sku,
  // not the unbundled suite. It adds more logic than it's worth for a small cohort
  // that will eventually go away.
  const suiteSkuId = skuIds && skuIds.length === 1 ? skuIds[0] : null;
  const showPricePerMonthPerSeat = useShowPricePerMonthPerSeat(suiteSkuId);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(Section, {
      "data-selenium-id": `${SuiteProductLevelIdentifiersMap[productLevel]}-merch-card`,
      className: "flex-column p-top-6",
      style: Object.assign({}, commonStyles, {
        borderTop: borderStyle,
        gridRowStart: 1,
        position: 'relative'
      }),
      children: [isCurrentPlanLevel && /*#__PURE__*/_jsx(CurrentPlanTag, {}), /*#__PURE__*/_jsx("div", {
        className: "display-flex align-center",
        children: /*#__PURE__*/_jsx(SectionTitle, {
          className: "m-bottom-0",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: titleKey
          })
        })
      })]
    }), /*#__PURE__*/_jsx(Section, {
      className: "p-top-6",
      style: Object.assign({}, commonStyles, {
        gridRowStart: 2
      }),
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: subtitleKey
      })
    }), /*#__PURE__*/_jsxs(Section, {
      className: "p-top-4",
      style: Object.assign({}, commonStyles, {
        gridRowStart: 3
      }),
      children: [/*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: !hasPricingAndPurchasingSucceeded,
        heights: [16, 23, 16],
        widths: [60, 70, 130],
        className: "flex-column",
        Content: CrmSuitePrice,
        contentProps: {
          productLevel,
          useLargePrice: true,
          showPricePerMonthPerSeat
        }
      }), suiteSkuId ? /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: !hasPricingAndPurchasingSucceeded,
        heights: [16, 23, 16],
        widths: [60, 70, 130],
        className: "flex-column",
        Content: IncludedSeatsDisplay,
        contentProps: {
          skuId: skuIds[0],
          queryParamTerm
        }
      }) : null]
    }), /*#__PURE__*/_jsx(Section, {
      className: "p-top-6",
      style: Object.assign({}, commonStyles, {
        gridRowStart: 4
      }),
      children: !isCurrentPlanLevel && /*#__PURE__*/_jsx("div", {
        className: "m-top-3",
        children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
          isLoading: !hasPricingAndPurchasingSucceeded,
          heights: [41],
          Content: SuiteTermSelectorContainer,
          contentProps: {
            productLevel,
            onChange: term => setQueryParamTerm(term),
            queryParamTerm,
            trackingSource: SUITE_CARD
          }
        })
      })
    }), /*#__PURE__*/_jsx(Section, {
      className: "p-top-4 p-bottom-6",
      style: Object.assign({}, commonStyles, {
        gridRowStart: 5
      }),
      children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: !hasPricingAndPurchasingSucceeded,
        heights: [47],
        className: "m-bottom-7",
        children: /*#__PURE__*/_jsx(SuiteTileCTAsContainer, {
          productLevel: productLevel,
          trackingSource: SUITE_CARD,
          ctaStyles: {
            width: '100%',
            lineHeight: '23px'
          },
          hubsSignupProductOverride: SuiteProductLevelIdentifiersMap[productLevel].split('-').join('').toLowerCase()
        })
      })
    }), /*#__PURE__*/_jsx(Section, {
      className: "p-bottom-6 flex-column align-center",
      style: Object.assign({}, commonStyles, {
        gridRowStart: 6
      })
    }), /*#__PURE__*/_jsx(Section, {
      className: "p-bottom-6 flex-column",
      style: Object.assign({}, commonStyles, {
        gridRowStart: 7
      })
    }), /*#__PURE__*/_jsx(Section, {
      className: "p-y-5",
      style: Object.assign({
        borderBottom: borderStyle,
        gridRowStart: 8
      }, commonStyles),
      children: /*#__PURE__*/_jsx(MobileSuiteBusinessObjectives, {
        productLevel: productLevel
      })
    })]
  });
};